import { useState } from 'react'
import type { AberturaTanque } from '../../../dominio/caminhaoTanque/perfil'
import type { EstadoTransporte } from '../../../dominio/produtos'
import { ACOES, acaoPorId, type AvaliacaoProcedimento, type IdAcao, type PassoDoAluno } from '../../../dominio/transbordo/procedimento'
import { IconeAlerta, IconeConcluido, IconeReiniciar } from '../../icones'
import { ValorCitado } from '../comum/ValorCitado'

interface Props {
  estado: EstadoTransporte
  aberturas: readonly AberturaTanque[]
  avaliacao: AvaliacaoProcedimento
  aoExecutar: (passo: PassoDoAluno) => void
  aoReiniciar: () => void
}

/** Botões das ações, na ordem que o ALUNO escolher — a ordem certa é o que se avalia. */
export function BotoesProcedimento({ estado, aberturas, aoExecutar }: Pick<Props, 'estado' | 'aberturas' | 'aoExecutar'>) {
  const conectaveis = aberturas.filter((a) => a.tipo !== 'valvulaAlivio' && a.tipo !== 'bocaVisita')
  const [escolha, setEscolha] = useState<Partial<Record<IdAcao, string>>>({})
  // embaralha a apresentação de forma estável (por nome), para a lista não entregar a ordem correta
  const acoes = [...ACOES].filter((a) => !a.somenteGasLiquefeito || estado === 'gasLiquefeitoPressurizado').sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'))
  return (
    <div className="procedimento__botoes">
      {acoes.map((acao) => (
        <div key={acao.id} className="procedimento__acao">
          <button
            type="button"
            className="btn-acao-cad btn-acao-cad--secundario"
            onClick={() => aoExecutar({ acao: acao.id, idAbertura: acao.usaAbertura ? escolha[acao.id] ?? conectaveis[0]?.id : undefined })}
          >
            {acao.nome}
          </button>
          {acao.usaAbertura && (
            <select
              aria-label={`Abertura para: ${acao.nome}`}
              value={escolha[acao.id] ?? conectaveis[0]?.id}
              onChange={(e) => setEscolha({ ...escolha, [acao.id]: e.target.value })}
            >
              {conectaveis.map((a) => (
                <option key={a.id} value={a.id}>{a.nome}</option>
              ))}
            </select>
          )}
        </div>
      ))}
    </div>
  )
}

/** O que o aluno fez, passo a passo, com o retorno de cada um. */
export function RegistroProcedimento({ avaliacao, aoReiniciar }: Pick<Props, 'avaliacao' | 'aoReiniciar'>) {
  return (
    <div className="grupo-controle">
      <div className="procedimento__cabecalho">
        <span className="titulo-controle">Sequência executada</span>
        <button type="button" className="btn-acao-cad btn-acao-cad--secundario" onClick={aoReiniciar} disabled={avaliacao.passos.length === 0}>
          <IconeReiniciar /> Recomeçar
        </button>
      </div>
      {avaliacao.passos.length === 0 ? (
        <p className="nota-controle">Nenhuma ação ainda. Escolha a primeira no grupo "Procedimento".</p>
      ) : (
        <ol className="procedimento__registro">
          {avaliacao.passos.map(({ passo, correto, mensagem }, i) => {
            const acao = acaoPorId(passo.acao)
            return (
              <li key={i} className={correto ? 'procedimento__passo--ok' : 'procedimento__passo--erro'}>
                {correto ? <IconeConcluido /> : <IconeAlerta />}
                <div>
                  <strong>{acao.nome}</strong>
                  {passo.idAbertura && <span className="nota-controle"> — {passo.idAbertura}</span>}
                  <p>{acao.fonte && correto ? <ValorCitado fonte={acao.fonte}>{mensagem}</ValorCitado> : mensagem}</p>
                </div>
              </li>
            )
          })}
        </ol>
      )}
      <p className={`feedback-colocacao${avaliacao.concluido && avaliacao.erros === 0 ? ' correta' : avaliacao.concluido ? ' proxima' : ''}`} role="status">
        <strong>
          {avaliacao.concluido
            ? avaliacao.erros === 0
              ? 'Transbordo concluído sem erros.'
              : `Transbordo concluído com ${avaliacao.erros} erro(s) no caminho.`
            : `Faltam ${avaliacao.pendentes.length} ação(ões).`}
        </strong>
        Sequência didática; siga o procedimento operacional da sua corporação.
      </p>
    </div>
  )
}
